import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/services/hooks";
import { getAllProducts } from "@/services/productServices/productActions";
import { getProductProperties } from "@/services/productPropertiesServices/ProductPropertiesActions";
import { getCategories } from "@/services/categoryServices/categoryAction";
import { getPosts } from "@/services/blogServices/blogActions";
import { getPagesByCurrentSite } from "@/services/pageServices/pageAction";
import {
  toCategoryOptions,
  toSubCategoryOptions,
  toPostOptions,
} from "../dropdownOptions";
import { narrowToCategoryPage, type PageTypeLike } from "./useDropdownOptions";

/**
 * useOptionalDropdownOptions — the dropdown options of HandleContent's
 * `status="optional"` host (SCRUM-2167, lifted out of the former Optional
 * host). The Required host's counterpart is useDropdownOptions.
 *
 * Pinned deviations from useDropdownOptions:
 *  - product SOURCE: the productproperties slice (with getAllProducts and
 *    getProductProperties fetched together), not categories.subCategory_by_id,
 *    and no sub_category trunk pool;
 *  - the fetch-if-empty effect runs once on mount (legacy mount-only), where
 *    the Required hook re-checks on size changes (SCRUM-571).
 *
 * Every option list is derived during render from its slice; nothing is
 * copied into state.
 */
export function useOptionalDropdownOptions(pageType?: PageTypeLike) {
  const dispatch = useAppDispatch();
  const products_redux = useAppSelector(
    (state) => state.productproperties.productproperties
  );
  const categories_redux = useAppSelector((state) => state.categories.categories);
  const posts_redux = useAppSelector((state) => state.blog.posts);
  const pages_redux = useAppSelector((state) => state.pages.pages);

  // Load specific data to redux — mount-only (pinned deviation, see above).
  useEffect(() => {
    if (products_redux.length === 0) {
      dispatch(getAllProducts());
      dispatch(getProductProperties());
    }
    if (categories_redux.length === 0) {
      dispatch(getCategories());
    }
    if (!posts_redux || posts_redux.length === 0) {
      dispatch(getPosts());
    }
    if (!pages_redux || pages_redux.length === 0) {
      dispatch(getPagesByCurrentSite());
    }
    // mount-only by design (pinned deviation); no eslint-disable here: the React
    // Compiler skips a function that suppresses a react-hooks rule.
  }, []);

  return {
    localProducts: narrowToCategoryPage(products_redux, pageType),
    localCategories: toCategoryOptions(categories_redux),
    localSubCategories: toSubCategoryOptions(categories_redux),
    localPosts: toPostOptions(posts_redux),
  };
}
